'use client'

import Placeholder from '@tiptap/extension-placeholder'
import { Markdown } from '@tiptap/markdown'
import { Plugin } from '@tiptap/pm/state'
import { type AnyExtension, type Editor, EditorContent, Extension, useEditor, useEditorState } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import StarterKit from '@tiptap/starter-kit'
import {
  Bold,
  ChevronLeft,
  ChevronRight,
  Heading1,
  Heading2,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Strikethrough,
  Type,
  Underline,
} from 'lucide-react'
import {
  forwardRef,
  type ReactNode,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { createPortal } from 'react-dom'

import { cn } from '../cn'
import { Drawer, DrawerContent, DrawerTitle } from './drawer'
import { proseThemeClass } from './prose-theme'

/** Prefix a scheme onto a bare domain so `ordo.com` does not resolve as a relative path. */
function normalizeLinkHref(raw: string): string {
  const href = raw.trim()
  if (!href || /^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith('/') || href.startsWith('#')) {
    return href
  }
  return `https://${href}`
}

/**
 * Where the top of what the user can see sits, in layout-viewport pixels.
 * `position: fixed; top: 0` anchors to the LAYOUT viewport, and iOS pans that
 * up to reveal a focused field above the keyboard — so a bar pinned to the
 * visible top must move down by this much. Never negative (rubber-band).
 */
function visibleViewportTop(view: { offsetTop: number }): number {
  return Math.max(0, Math.round(view.offsetTop))
}

const coarsePointerQuery = '(pointer: coarse)'

/** Touch-first device (phone, tablet): no hover, and the OS owns the selection callout. */
function useCoarsePointer(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const mql = window.matchMedia(coarsePointerQuery)
      mql.addEventListener('change', onChange)
      return () => mql.removeEventListener('change', onChange)
    },
    () => window.matchMedia(coarsePointerQuery).matches,
    () => false,
  )
}

/** Live {@link visibleViewportTop}; tracks the keyboard opening, resizing and iOS's viewport pan. */
function useVisibleViewportTop(): number {
  const [top, setTop] = useState(0)
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) {
      return
    }
    const update = () => setTop(visibleViewportTop(vv))
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    }
  }, [])
  return top
}

/**
 * Asana's link dialog: link the selection, or replace it with `text` when the
 * user retyped it. An empty href removes the link.
 */
function applyLink(editor: Editor, { text, href }: { text: string; href: string }) {
  const url = normalizeLinkHref(href)
  const chain = editor.chain().focus().extendMarkRange('link')
  if (!url) {
    chain.unsetLink().run()
    return
  }
  const { from, to } = editor.state.selection
  const selected = editor.state.doc.textBetween(from, to)
  const label = text.trim() || selected || url
  if (label === selected && from !== to) {
    chain.setLink({ href: url }).run()
    return
  }
  chain
    .insertContent({ type: 'text', text: label, marks: [{ type: 'link', attrs: { href: url } }] })
    .unsetMark('link')
    .run()
}

/**
 * Extensions for a WYSIWYG editor whose source of truth is MARKDOWN, not HTML.
 * `RichTextEditor` stores HTML; use this one wherever the stored value is read
 * as markdown elsewhere (agent tools, sync jobs, react-markdown renderers).
 * Exported so tests can build a headless editor with the exact same schema.
 *
 * Shortcuts follow Asana's text formatting: ⌘B ⌘I ⌘U (StarterKit), ⌘⇧X
 * strikethrough, ⌘⇧M monospace, ⌘⇧8 / ⌘⇧7 lists (StarterKit), ⌘] / ⌘[ list
 * indent, ⌘K link, and selecting text then typing a backtick for monospace.
 */
function markdownEditorExtensions(opts: { placeholder?: string; onLinkShortcut?: () => void } = {}): AnyExtension[] {
  const { placeholder, onLinkShortcut } = opts
  return [
    StarterKit.configure({
      // StarterKit 3 bundles Link and Underline. Links open on click like the
      // rendered markdown did; autolink turns a typed URL into a link.
      link: { openOnClick: true, autolink: true },
    }),
    Markdown,
    Extension.create({
      name: 'asanaFormatting',
      // Above StarterKit, so the backtick rule below runs before the inline-code
      // input rule (editorProps handlers run after every extension plugin).
      priority: 1000,
      addProseMirrorPlugins() {
        return [
          new Plugin({
            props: {
              // Select text, type a backtick: toggle monospace instead of
              // replacing the selection.
              handleTextInput: (view, from, to, text) => {
                const code = view.state.schema.marks['code']
                if (text !== '`' || from === to || !code) {
                  return false
                }
                const tr = view.state.doc.rangeHasMark(from, to, code)
                  ? view.state.tr.removeMark(from, to, code)
                  : view.state.tr.addMark(from, to, code.create())
                view.dispatch(tr)
                return true
              },
            },
          }),
        ]
      },
      addKeyboardShortcuts() {
        return {
          'Mod-Shift-x': () => this.editor.commands.toggleStrike(),
          'Mod-Shift-m': () => this.editor.commands.toggleCode(),
          'Mod-]': () => this.editor.commands.sinkListItem('listItem'),
          'Mod-[': () => this.editor.commands.liftListItem('listItem'),
          'Mod-k': () => {
            onLinkShortcut?.()
            return true
          },
        }
      },
    }),
    ...(placeholder ? [Placeholder.configure({ placeholder })] : []),
  ]
}

interface MarkdownEditorProps {
  /** Markdown source. */
  value: string
  /** Fires with the serialized markdown on USER edits only — never on a `value` sync. */
  onChange: (markdown: string) => void
  onFocus?: () => void
  onBlur?: () => void
  placeholder?: string
  /** Classes for the bordered container. */
  className?: string
}

interface MarkdownEditorHandle {
  focus: () => void
  blur: () => void
}

const placeholderClass =
  '[&_p.is-editor-empty:first-child]:before:content-[attr(data-placeholder)] [&_p.is-editor-empty:first-child]:before:text-muted-foreground/50 [&_p.is-editor-empty:first-child]:before:float-left [&_p.is-editor-empty:first-child]:before:h-0 [&_p.is-editor-empty:first-child]:before:pointer-events-none'

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
const mod = isMac ? '⌘' : 'Ctrl+'
const shift = isMac ? '⇧' : 'Shift+'

type ToolbarVariant = 'bubble' | 'dock'

function ToolbarButton({
  label,
  active,
  onClick,
  variant,
  children,
}: {
  label: string
  active?: boolean
  onClick: () => void
  variant: ToolbarVariant
  children: ReactNode
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      // Keep the editor selection: a focus move would collapse what we format.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        'flex shrink-0 items-center justify-center rounded-md transition-colors',
        // 44px is the minimum reliable touch target.
        variant === 'dock' ? 'h-11 w-11' : 'h-8 w-8',
        active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
      )}>
      {children}
    </button>
  )
}

function ToolbarDivider() {
  return <span className="mx-1 h-5 w-px shrink-0 bg-border" aria-hidden />
}

function LinkForm({ editor, onDone, variant }: { editor: Editor; onDone: () => void; variant: ToolbarVariant }) {
  const { from, to } = editor.state.selection
  const [text, setText] = useState(() => editor.state.doc.textBetween(from, to))
  const [href, setHref] = useState(() => (editor.getAttributes('link')['href'] as string | undefined) ?? '')
  const urlRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    urlRef.current?.focus()
  }, [])

  const close = () => {
    onDone()
    editor.commands.focus()
  }
  // iOS zooms into any field whose text is under 16px.
  const inputClass = cn(
    'rounded-md border border-border bg-background px-2 outline-none focus:border-primary/40',
    variant === 'dock' ? 'h-11 text-base' : 'h-8 text-sm pointer-coarse:text-base',
  )

  return (
    <form
      className={cn('flex flex-col gap-2', variant === 'dock' ? 'w-full p-4' : 'w-72 p-2')}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          // Close just the form; the task panel's own Escape must not see it.
          e.preventDefault()
          e.stopPropagation()
          close()
        }
      }}
      onSubmit={(e) => {
        e.preventDefault()
        applyLink(editor, { text, href })
        onDone()
      }}>
      <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Text" className={inputClass} />
      <input
        ref={urlRef}
        value={href}
        onChange={(e) => setHref(e.target.value)}
        placeholder="Paste a link"
        className={inputClass}
      />
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={close}
          className={cn(
            'rounded-md px-2 text-muted-foreground hover:bg-accent hover:text-foreground',
            variant === 'dock' ? 'h-11 text-sm' : 'h-7 text-xs',
          )}>
          Cancel
        </button>
        <button
          type="submit"
          className={cn(
            'rounded-md bg-primary px-3 font-medium text-primary-foreground',
            variant === 'dock' ? 'h-11 px-5 text-sm' : 'h-7 text-xs',
          )}>
          {href.trim() || !editor.isActive('link') ? 'Save' : 'Remove link'}
        </button>
      </div>
    </form>
  )
}

/** Asana's selection toolbar: marks, block type, lists, link. */
function FormattingToolbar({
  editor,
  onLink,
  variant,
}: {
  editor: Editor
  onLink: () => void
  variant: ToolbarVariant
}) {
  const state = useEditorState({
    editor,
    selector: ({ editor: ed }) => ({
      bold: ed.isActive('bold'),
      italic: ed.isActive('italic'),
      underline: ed.isActive('underline'),
      strike: ed.isActive('strike'),
      h1: ed.isActive('heading', { level: 1 }),
      h2: ed.isActive('heading', { level: 2 }),
      paragraph: ed.isActive('paragraph') && !ed.isActive('bulletList') && !ed.isActive('orderedList'),
      bulletList: ed.isActive('bulletList'),
      orderedList: ed.isActive('orderedList'),
      link: ed.isActive('link'),
    }),
  })
  const run = () => editor.chain().focus()

  return (
    <div className={cn('flex items-center', variant === 'bubble' && 'p-1')}>
      <ToolbarButton
        variant={variant}
        label={`Bold (${mod}B)`}
        active={state.bold}
        onClick={() => run().toggleBold().run()}>
        <Bold className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        variant={variant}
        label={`Italic (${mod}I)`}
        active={state.italic}
        onClick={() => run().toggleItalic().run()}>
        <Italic className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        variant={variant}
        label={`Underline (${mod}U)`}
        active={state.underline}
        onClick={() => run().toggleUnderline().run()}>
        <Underline className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        variant={variant}
        label={`Strikethrough (${mod}${shift}X)`}
        active={state.strike}
        onClick={() => run().toggleStrike().run()}>
        <Strikethrough className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarDivider />
      <ToolbarButton
        variant={variant}
        label="Normal text"
        active={state.paragraph}
        onClick={() => run().setParagraph().run()}>
        <Type className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        variant={variant}
        label="Heading 1"
        active={state.h1}
        onClick={() => run().toggleHeading({ level: 1 }).run()}>
        <Heading1 className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        variant={variant}
        label="Heading 2"
        active={state.h2}
        onClick={() => run().toggleHeading({ level: 2 }).run()}>
        <Heading2 className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        variant={variant}
        label={`Bulleted list (${mod}${shift}8)`}
        active={state.bulletList}
        onClick={() => run().toggleBulletList().run()}>
        <List className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        variant={variant}
        label={`Numbered list (${mod}${shift}7)`}
        active={state.orderedList}
        onClick={() => run().toggleOrderedList().run()}>
        <ListOrdered className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarDivider />
      <ToolbarButton variant={variant} label={`Link (${mod}K)`} active={state.link} onClick={onLink}>
        <LinkIcon className="h-4 w-4" />
      </ToolbarButton>
    </div>
  )
}

/**
 * Touch layout: while the editor is focused the formatting bar pins to the TOP
 * of the visible screen. Not the bottom: iOS 26 floats its own form bar
 * (up / down / done) over the bottom of the visual viewport, a page cannot
 * measure it, and a bar docked on the keyboard rendered behind it. Not a
 * floating bubble either: it collides with the OS copy/paste callout above
 * the selection. Done replaces Escape and ⌘↵, which a phone keyboard lacks.
 */
/** Which edges of a horizontal scroller still hide buttons. 1px slack absorbs subpixel widths. */
function scrollEdges(el: { scrollLeft: number; scrollWidth: number; clientWidth: number }): {
  left: boolean
  right: boolean
} {
  return { left: el.scrollLeft > 1, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1 }
}

function ScrollArrow({ side, onClick }: { side: 'left' | 'right'; onClick: () => void }) {
  const Icon = side === 'left' ? ChevronLeft : ChevronRight
  return (
    <button
      type="button"
      aria-label={side === 'left' ? 'Earlier formatting options' : 'More formatting options'}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        'absolute inset-y-0 z-10 flex w-10 items-center text-muted-foreground',
        // A fade from the bar color makes the cut-off buttons read as "more this way".
        side === 'left'
          ? 'left-0 justify-start bg-gradient-to-r from-popover from-60% to-transparent pl-1'
          : 'right-0 justify-end bg-gradient-to-l from-popover from-60% to-transparent pr-1',
      )}>
      <Icon className="h-5 w-5" />
    </button>
  )
}

function TouchToolbar({ editor, onLink }: { editor: Editor; onLink: () => void }) {
  const top = useVisibleViewportTop()
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [edges, setEdges] = useState({ left: false, right: false })

  useEffect(() => {
    const el = scrollerRef.current
    if (!el) {
      return
    }
    const update = () => setEdges(scrollEdges(el))
    update()
    el.addEventListener('scroll', update, { passive: true })
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update)
    observer?.observe(el)
    return () => {
      el.removeEventListener('scroll', update)
      observer?.disconnect()
    }
  }, [])

  // Swiping scrolls the row; the arrows are the visible, tappable way to do it.
  const page = (direction: 1 | -1) => {
    const el = scrollerRef.current
    el?.scrollBy({ left: direction * el.clientWidth * 0.75, behavior: 'smooth' })
  }

  return createPortal(
    <div
      role="toolbar"
      aria-label="Formatting"
      style={{ top }}
      // At the true top (an installed PWA) clear the status bar / notch.
      className={cn(
        'fixed inset-x-0 z-modal flex items-center border-b border-border bg-popover text-popover-foreground shadow-sm',
        top === 0 && 'pt-[env(safe-area-inset-top)]',
      )}>
      <div className="relative flex min-w-0 flex-1">
        <div
          ref={scrollerRef}
          className="flex min-w-0 flex-1 touch-pan-x overflow-x-auto overscroll-x-contain px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <FormattingToolbar editor={editor} onLink={onLink} variant="dock" />
        </div>
        {edges.left ? <ScrollArrow side="left" onClick={() => page(-1)} /> : null}
        {edges.right ? <ScrollArrow side="right" onClick={() => page(1)} /> : null}
      </div>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => editor.commands.blur()}
        className="h-11 shrink-0 border-l border-border px-4 text-sm font-semibold text-primary">
        Done
      </button>
    </div>,
    document.body,
  )
}

const MarkdownEditor = forwardRef<MarkdownEditorHandle, MarkdownEditorProps>(function MarkdownEditor(
  { value, onChange, onFocus, onBlur, placeholder, className },
  ref,
) {
  // Callbacks change identity every render; the editor is built once.
  const handlers = useRef({ onChange, onFocus, onBlur })
  handlers.current = { onChange, onFocus, onBlur }
  const [linkOpen, setLinkOpen] = useState(false)
  // The bubble menu's shouldShow runs outside React; it reads the ref.
  const linkOpenRef = useRef(false)
  linkOpenRef.current = linkOpen
  const openLinkRef = useRef<() => void>(() => {})
  const coarse = useCoarsePointer()
  const [focused, setFocused] = useState(false)

  const editor = useEditor({
    extensions: markdownEditorExtensions({ placeholder, onLinkShortcut: () => openLinkRef.current() }),
    content: value,
    contentType: 'markdown',
    immediatelyRender: false,
    onUpdate: ({ editor: ed }) => handlers.current.onChange(ed.isEmpty ? '' : ed.getMarkdown()),
    onFocus: () => {
      setFocused(true)
      handlers.current.onFocus?.()
    },
    onBlur: () => {
      setFocused(false)
      handlers.current.onBlur?.()
    },
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-sm max-w-none break-words [overflow-wrap:anywhere] outline-none min-h-[inherit]',
          // iOS Safari zooms the page into any focused field under 16px.
          'pointer-coarse:text-base!',
          proseThemeClass,
          placeholderClass,
        ),
      },
      // Keep the caret clear of the touch toolbar pinned to the top when typing scrolls.
      scrollMargin: { top: 64, bottom: 16, left: 0, right: 0 },
      handleKeyDown: (view, event) => {
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
          ;(view.dom as HTMLElement).blur()
          return true
        }
        return false
      },
    },
  })

  openLinkRef.current = () => {
    if (!editor) {
      return
    }
    setLinkOpen(true)
    if (!coarse) {
      // With an empty selection the menu's own rule hides it; force it open.
      editor.view.dispatch(editor.state.tr.setMeta('bubbleMenu', 'show'))
    }
  }

  useImperativeHandle(
    ref,
    () => ({
      focus: () => editor?.commands.focus('end'),
      blur: () => editor?.commands.blur(),
    }),
    [editor],
  )

  // Follow an external value (task switch, server revalidation) only while the
  // user is not typing, so a refetch can never rewrite the doc under the caret.
  // `emitUpdate: false` keeps the sync from reading as a user edit.
  useEffect(() => {
    if (!editor || editor.isFocused) {
      return
    }
    const current = editor.isEmpty ? '' : editor.getMarkdown()
    if (current !== value) {
      editor.commands.setContent(value, { contentType: 'markdown', emitUpdate: false })
    }
  }, [editor, value])

  return (
    <div
      className={cn(
        'w-full rounded-md border border-border px-3 py-2 text-sm cursor-text transition-colors hover:border-primary/40 focus-within:border-primary/40 focus-within:ring-2 focus-within:ring-primary/20',
        className,
      )}
      onMouseDown={(e) => {
        // Clicking the padding (or empty space under short content) focuses
        // the editor instead of doing nothing.
        if (editor && e.target === e.currentTarget) {
          e.preventDefault()
          editor.commands.focus('end')
        }
      }}>
      {editor && coarse && focused ? <TouchToolbar editor={editor} onLink={() => openLinkRef.current()} /> : null}
      {editor && coarse ? (
        <Drawer open={linkOpen} onOpenChange={setLinkOpen} shouldScaleBackground={false}>
          <DrawerContent>
            <DrawerTitle className="px-4 pt-3 text-base font-semibold">Link</DrawerTitle>
            {linkOpen ? <LinkForm editor={editor} variant="dock" onDone={() => setLinkOpen(false)} /> : null}
          </DrawerContent>
        </Drawer>
      ) : null}
      {editor && !coarse ? (
        <BubbleMenu
          editor={editor}
          // Portaled: the task panel's scroll container is overflow-hidden.
          appendTo={() => document.body}
          shouldShow={({ view, state, element }) => {
            const focused = view.hasFocus() || element.contains(document.activeElement)
            if (!focused || !editor.isEditable) {
              return false
            }
            if (linkOpenRef.current) {
              return true
            }
            const { from, to, empty } = state.selection
            return !empty && state.doc.textBetween(from, to).length > 0
          }}
          options={{ placement: 'top-start', offset: 8, onHide: () => setLinkOpen(false) }}
          className="z-modal rounded-lg border border-border bg-popover text-popover-foreground shadow-md">
          {linkOpen ? (
            <LinkForm editor={editor} variant="bubble" onDone={() => setLinkOpen(false)} />
          ) : (
            <FormattingToolbar editor={editor} variant="bubble" onLink={() => openLinkRef.current()} />
          )}
        </BubbleMenu>
      ) : null}
      <EditorContent editor={editor} className="min-h-[inherit]" />
    </div>
  )
})

export {
  applyLink,
  MarkdownEditor,
  markdownEditorExtensions,
  type MarkdownEditorHandle,
  type MarkdownEditorProps,
  normalizeLinkHref,
  scrollEdges,
  visibleViewportTop,
}
